// World units are feet. The show, the builder and the rules all speak feet.
//
// Frame: x runs along the booth width, left to right as seen from the main
// aisle, z runs from the back wall (0) toward the main aisle (depth). y is up.
// Per the Hall G floor plan, booth 44349 (a 10x10) shares our left side at
// x < 0, the cross aisle is on the right at x > width, the main aisle is at
// z > depth, and the back wall stands against the hall perimeter.

/** Dimensions from the SHOT 2027 display regulations (issue #1285). */
export const BOOTH = {
  nominalWidth: 20,
  nominalDepth: 10,
  /** 20' less 3" per side rail. */
  width: 19.5,
  /** 10' less 9" at the rear for utility access. */
  depth: 9.25,
} as const;

/** The exhibitor sharing our left side, from the Hall G floor plan: a 10x10. */
export const NEIGHBOR = { booth: '44349', width: 10 } as const;

/** Not in the regulations; the usual show standard. Labelled as assumed in the UI. */
export const AISLE_WIDTH = 10;

/** Hall G (booths 40000–45000) caps linear booths at 10'. */
export const WALL_HEIGHTS = [8, 10] as const;
export type WallHeight = (typeof WALL_HEIGHTS)[number];
export const HALL_G_MAX_HEIGHT: WallHeight = 10;
/** What the model opens with: the full height Hall G allows. */
export const DEFAULT_WALL_HEIGHT: WallHeight = 10;

/** Anything over 8' facing the neighbor must sit 10' from the neighbor's booth. */
export const NEIGHBOR_GRAPHICS_RULE = { aboveHeight: 8, setback: 10 } as const;

/** Eye height for the walk view. */
export const EYE_HEIGHT = 5.4;

export type FixtureKind = 'counter' | 'kiosk' | 'closet' | 'tv';

/**
 * What a demo station's laptop shows. Each is a real capture of the app
 * (`public/app-*.png`); `search` is also what the LED wall shows.
 */
export type StationScreen = 'search' | 'firearms-finder' | 'ammo-finder';

export interface Fixture {
  id: string;
  kind: FixtureKind;
  /** Demo stations only: which app page the laptop is open to. */
  screen?: StationScreen;
  /** Footprint centre. */
  x: number;
  z: number;
  /** Footprint, before rotation. */
  w: number;
  d: number;
  h: number;
  /** Rotation about y, radians. */
  rot: number;
}

export interface StaffSpot {
  id: string;
  x: number;
  z: number;
}

/** Layout A: counter at the open corner, two kiosks, closet in the closed back corner. */
/**
 * The counter carries the third demo laptop on its left, as a fraction of its
 * width from the centre; the sim's queue forms in front of that spot.
 */
export const COUNTER_LAPTOP_OFFSET = 0.3;

export const LAYOUT_A: Fixture[] = [
  {
    id: 'counter',
    kind: 'counter',
    screen: 'firearms-finder',
    x: 16.3,
    z: 7.4,
    w: 5,
    d: 2,
    h: 3.5,
    rot: 0,
  },
  {
    id: 'kiosk-front',
    kind: 'kiosk',
    screen: 'search',
    x: 6.0,
    z: 6.4,
    w: 2,
    d: 2,
    h: 3.6,
    rot: 0,
  },
  {
    id: 'kiosk-back',
    kind: 'kiosk',
    screen: 'ammo-finder',
    x: 2.9,
    z: 5.2,
    w: 2,
    d: 2,
    h: 3.6,
    rot: 0,
  },
  {
    id: 'closet',
    kind: 'closet',
    x: 1.4,
    z: 1.4,
    w: 2.75,
    d: 2.75,
    h: 8,
    rot: 0,
  },
  {
    id: 'tv',
    kind: 'tv',
    screen: 'search',
    x: BOOTH.width / 2,
    z: 1.3,
    w: 3,
    d: 2,
    h: 6.5,
    rot: 0,
  },
];

export const STAFF_DEFAULT: StaffSpot[] = [
  { id: 'staff-1', x: 17.8, z: 5.4 },
  { id: 'staff-2', x: 13.3, z: 8.4 },
  { id: 'staff-3', x: 7.2, z: 6.4 },
  { id: 'staff-4', x: 4.3, z: 8.0 },
];

/** How far a staffer can comfortably greet from where they stand. */
export const STAFF_REACH = 4;

/**
 * The display: a 75" 16:9 commercial panel on a rolling floor stand in front
 * of the back wall, centred. 75" diagonal is 65.4" × 36.8" of picture, so
 * 5.45' × 3.07'. The stand carries it with its centre at 4.9', eye height
 * from the aisle, so the bottom sits at 3.37' and the top at 6.43'.
 */
export const TV = {
  diagonalInches: 75,
  aspect: 16 / 9,
  centreHeight: 4.9,
} as const;
const tvWidthFt = (TV.diagonalInches * (16 / Math.hypot(16, 9))) / 12;
export const SCREEN = {
  x: BOOTH.width / 2,
  width: tvWidthFt,
  height: tvWidthFt / TV.aspect,
  bottom: TV.centreHeight - tvWidthFt / TV.aspect / 2,
} as const;

/** The app capture the TV shows is 1440×962; it is fitted to the TV's height. */
export const SCREEN_IMAGE_ASPECT = 1440 / 962;

/** Where attendees stand to watch the screen: mid-floor, clear of the front kiosk. */
export const WATCH_SPOT = {
  x: SCREEN.x + 0.6,
  z: BOOTH.depth - 3.6,
  spread: 2,
} as const;
